import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { formatDataCivil } from "@/data/beneficios";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";

interface Linha {
  linha: number;
  nome: string;
  acao: "atualizar" | "sem_alteracao" | "erro";
  de: string | null;
  para: string | null;
  erros: string[];
}

interface Resultado {
  confirmado: boolean;
  linhas: Linha[];
  resumo: Record<Linha["acao"], number>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ORDEM: Record<Linha["acao"], number> = { erro: 0, atualizar: 1, sem_alteracao: 2 };

/** Datas de admissão (PJ e CLT) em lote: planilha com CPF (ou e-mail) e a data. */
export const ImportarAdmissoesModal = ({ open, onClose, onSuccess }: Props) => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Resultado | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (open) return;
    setArquivo(null);
    setPrevia(null);
  }, [open]);

  const enviar = (f: File, confirmar: boolean) => {
    const fd = new FormData();
    fd.append("arquivo", f);
    return api.postForm<Resultado>(`/colaboradores/importar-admissoes${confirmar ? "?confirmar=true" : ""}`, fd);
  };

  const escolher = async (f: File | undefined) => {
    if (!f) return;
    setArquivo(f);
    setPrevia(null);
    setCarregando(true);
    try {
      setPrevia(await enviar(f, false));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ler a planilha");
      setArquivo(null);
    } finally {
      setCarregando(false);
    }
  };

  const confirmar = async () => {
    if (!arquivo) return;
    setCarregando(true);
    try {
      const r = await enviar(arquivo, true);
      toast.success(`${r.resumo.atualizar} data(s) de admissão gravada(s)`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao importar");
    } finally {
      setCarregando(false);
    }
  };

  const baixarModelo = () => {
    const csv = "﻿CPF;E-mail;Nome;Data de admissão\r\n123.456.789-09;maria@empresa.com.br;Maria Souza;15/03/2024\r\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "modelo-admissoes.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const linhas = [...(previa?.linhas ?? [])].sort((a, b) => ORDEM[a.acao] - ORDEM[b.acao] || a.linha - b.linha);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar datas de admissão</DialogTitle>
          <DialogDescription>
            Para PJ e CLT (a do CLT vai para Dados CLT). Basta a coluna <strong>CPF</strong> ou <strong>E-mail</strong>{" "}
            e a coluna <strong>Data de admissão</strong> (dd/mm/aaaa). A data é a base da experiência, do proporcional
            da folha e da bonificação. CLT com folha fechada no mês da data precisa reabrir a competência.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={baixarModelo}>
            <Download className="mr-1.5 h-4 w-4" />
            Baixar modelo
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted/50">
            <FileSpreadsheet className="h-4 w-4" />
            {arquivo ? arquivo.name : "Selecionar planilha"}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                void escolher(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
          {carregando && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {previa && (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="default">{previa.resumo.atualizar} a gravar</Badge>
              <Badge variant="secondary">{previa.resumo.sem_alteracao} sem alteração</Badge>
              {previa.resumo.erro > 0 && <Badge variant="destructive">{previa.resumo.erro} com erro</Badge>}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Linha</TableHead>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Admissão</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map((l) => (
                  <TableRow key={l.linha}>
                    <TableCell className="text-muted-foreground">{l.linha}</TableCell>
                    <TableCell className="font-medium">{l.nome || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {l.acao === "erro" ? (
                        <span className="text-destructive">{l.erros.join(" ")}</span>
                      ) : l.acao === "sem_alteracao" ? (
                        <span className="text-muted-foreground">{formatDataCivil(l.para!)} (igual)</span>
                      ) : (
                        <>
                          {l.de ? `${formatDataCivil(l.de)} → ` : ""}
                          <strong>{formatDataCivil(l.para!)}</strong>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {previa.resumo.erro > 0 && (
              <p className="text-xs text-muted-foreground">
                Linhas com erro são ignoradas; as demais podem ser gravadas mesmo assim.
              </p>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={carregando}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} disabled={carregando || !previa || previa.resumo.atualizar === 0}>
            {carregando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Gravar {previa?.resumo.atualizar ?? 0} data(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
